import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { crApi, Employee, CRDetail } from '../services/crApi';

const EvaluationPersonal: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useAlert();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  const [myEmployee, setMyEmployee] = useState<Employee | null>(null);
  const [crDetails, setCrDetails] = useState<CRDetail[]>([]);
  const [monthlyMM, setMonthlyMM] = useState<number[]>(new Array(12).fill(0));
  const [totalMM, setTotalMM] = useState<number>(0);
  const [utilityPct, setUtilityPct] = useState<number>(0);

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        const years = await crApi.getAvailableYears();
        setAvailableYears(years);
        if (years.length > 0 && !years.includes(selectedYear)) {
          setSelectedYear(years[0]);
        }
      } catch {
        setAvailableYears([new Date().getFullYear()]);
      }
    };
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const employeeCRList = await crApi.getEmployeeCRList(selectedYear);
        const currentEmployee = employeeCRList.find(emp => emp.id === user?.id);

        setMyEmployee(currentEmployee || null);

        if (currentEmployee?.calculationDetails) {
          try {
            const parsed = JSON.parse(currentEmployee.calculationDetails);
            const projects = parsed.projects || parsed;

            const formatted: CRDetail[] = projects.map((detail: any) => ({
              employeeId: user?.id || 0,
              projectId: detail.projectId || 0,
              projectName: detail.projectName,
              projectRevenue: detail.billingAmount || detail.employeeRevenue || detail.revenue || 0,
              inputCost: detail.annualLaborCost || 0,
              cr: detail.projectCR || 0,
              yearlyMM: detail.yearlyMM || 0,
            })).sort((a: CRDetail, b: CRDetail) => b.projectRevenue - a.projectRevenue);

            setCrDetails(formatted);

            const mm = new Array(12).fill(0);
            projects.forEach((project: any) => {
              if (project.monthlyDetails) {
                project.monthlyDetails.forEach((md: any) => {
                  if (md.month >= 1 && md.month <= 12) {
                    mm[md.month - 1] += md.mm || 0;
                  }
                });
              }
            });
            const total = mm.reduce((sum: number, v: number) => sum + v, 0);
            setMonthlyMM(mm);
            setTotalMM(total);
            setUtilityPct((total / 12) * 100);
          } catch {
            setCrDetails([]);
            setMonthlyMM(new Array(12).fill(0));
            setTotalMM(0);
            setUtilityPct(0);
          }
        } else {
          setCrDetails([]);
          setMonthlyMM(new Array(12).fill(0));
          setTotalMM(0);
          setUtilityPct(0);
        }
      } catch {
        showToast('데이터를 불러오는데 실패했습니다.', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id, selectedYear]);

  const formatNumber = (num: number) => new Intl.NumberFormat('ko-KR').format(Math.round(num));
  const formatMM = (num: number) => Number(num || 0).toFixed(1);

  const totalBilling = crDetails.reduce((sum, d) => sum + d.projectRevenue, 0);
  const totalYearlyMM = crDetails.reduce((sum, d) => sum + d.yearlyMM, 0);
  const annualLaborCost = crDetails[0]?.inputCost || 0;
  const coveredRevenuePct = annualLaborCost > 0 ? (totalBilling / annualLaborCost) * 100 : 0;
  const mmPct = (totalYearlyMM / 12) * 100;

  if (!user?.id && !loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-slate-600 mb-2">직원 정보를 찾을 수 없습니다.</p>
          <p className="text-sm text-slate-500">로그인 정보를 확인해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <header className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">내 평가</h1>
          <p className="text-slate-500 mt-1">나의 정량 평가 현황을 확인할 수 있습니다.</p>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {availableYears.filter(year => year >= 2025).map(year => (
            <option key={year} value={year}>{year}년</option>
          ))}
        </select>
      </header>

      {/* 연간 Covered Revenue */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800">연간 Covered Revenue</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50">
              <tr>
                <th className="px-6 py-3 font-medium text-left">사번</th>
                <th className="px-6 py-3 font-medium text-left">이름</th>
                <th className="px-6 py-3 font-medium text-left">직무</th>
                <th className="px-6 py-3 font-medium text-left">직급</th>
                <th className="px-6 py-3 font-medium text-right">CR (%)</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
                      <p>데이터를 불러오는 중...</p>
                    </div>
                  </td>
                </tr>
              ) : !myEmployee ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    <p className="font-medium">데이터가 없습니다.</p>
                  </td>
                </tr>
              ) : (
                <tr
                  className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setIsPanelOpen(true)}
                >
                  <td className="px-6 py-4">{myEmployee.emp_no}</td>
                  <td className="px-6 py-4 font-semibold text-slate-900">{myEmployee.name}</td>
                  <td className="px-6 py-4">{myEmployee.department}</td>
                  <td className="px-6 py-4">{myEmployee.position}</td>
                  <td className={`px-6 py-4 text-right font-bold ${Number(myEmployee.totalCR || 0) < 100 ? 'text-red-600' : 'text-slate-900'}`}>
                    {Number(myEmployee.totalCR || 0).toFixed(1)}%
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 월별 Utility */}
      {!loading && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-800">월별 Utility</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  {Array.from({ length: 12 }, (_, i) => (
                    <th key={i} className="px-3 py-3 font-medium text-right whitespace-nowrap">{i + 1}월</th>
                  ))}
                  <th className="px-4 py-3 font-medium text-right whitespace-nowrap">총투입MM</th>
                  <th className="px-4 py-3 font-medium text-right whitespace-nowrap">Utility %</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <tr>
                  {monthlyMM.map((mm, i) => (
                    <td key={i} className={`px-3 py-3 text-right whitespace-nowrap ${mm > 1.2 ? 'text-red-600 font-bold' : ''}`}>
                      {mm > 0 ? mm.toFixed(1) : '-'}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{totalMM.toFixed(1)}</td>
                  <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${utilityPct < 70 ? 'text-red-600' : ''}`}>
                    {utilityPct.toFixed(1)}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CR 상세 패널 */}
      {isPanelOpen && (
        <div className="fixed inset-0 z-30">
          <div className="absolute inset-0 bg-black bg-opacity-20" onClick={() => setIsPanelOpen(false)}></div>
          <div className="absolute top-0 right-0 h-full w-full max-w-5xl bg-white shadow-2xl flex flex-col transform transition-transform duration-300">
            <header className="h-16 flex-shrink-0 border-b border-slate-200 flex items-center justify-between px-6">
              <h2 className="text-lg font-bold">{myEmployee?.name} CR상세 내역</h2>
              <button
                onClick={() => setIsPanelOpen(false)}
                className="p-2 rounded-full text-slate-500 hover:bg-slate-100 text-2xl"
              >
                ×
              </button>
            </header>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 font-medium text-left">참여 프로젝트명</th>
                        <th className="px-6 py-3 font-medium text-right whitespace-nowrap">빌링액</th>
                        <th className="px-6 py-3 font-medium text-right whitespace-nowrap">투입MM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crDetails.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-8 text-center text-slate-500">
                            참여한 프로젝트가 없습니다.
                          </td>
                        </tr>
                      ) : (
                        crDetails.map((detail, index) => (
                          <tr key={index} className="border-t border-slate-200">
                            <td className="px-6 py-4 font-semibold text-slate-800">{detail.projectName}</td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">{formatNumber(detail.projectRevenue)}</td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">{formatMM(detail.yearlyMM)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {crDetails.length > 0 && (
                      <tfoot>
                        <tr className="border-t-2 border-slate-300 bg-slate-50">
                          <td className="px-6 py-3 font-semibold text-slate-700">Revenue & Utility 합계</td>
                          <td className="px-6 py-3 text-right font-semibold">{formatNumber(totalBilling)}</td>
                          <td className="px-6 py-3 text-right font-semibold">{formatMM(totalYearlyMM)}</td>
                        </tr>
                        <tr className="border-t border-slate-200 bg-slate-50">
                          <td className="px-6 py-3 font-semibold text-slate-700">연간 인력원가</td>
                          <td className="px-6 py-3 text-right font-semibold">{formatNumber(annualLaborCost)}</td>
                          <td className="px-6 py-3 text-right font-semibold">12</td>
                        </tr>
                        <tr className="border-t-2 border-slate-400 bg-white">
                          <td className="px-6 py-4 font-bold text-slate-900 text-base">Covered Revenue</td>
                          <td className={`px-6 py-4 text-right font-bold text-base ${coveredRevenuePct < 100 ? 'text-red-600' : 'text-slate-900'}`}>
                            {coveredRevenuePct.toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 text-right font-bold text-base text-slate-900">
                            {mmPct.toFixed(1)}%
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationPersonal;
